import { ordinal } from '../../utils/format';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  Stack,
  Pagination,
  Button,
} from '@mui/material';

interface MemberItem {
  bioguide_id: string;
  first_name: string;
  last_name: string;
  party?: string;
  state?: string;
  district?: string;
  image_url?: string;
}

interface BillItem {
  congress: number;
  bill_type: string;
  bill_number: string;
  title?: string;
  latest_action_text?: string;
  latest_action_date?: string;
  sponsor_name?: string;
  cosponsors_count?: number;
  summary?: string | null;
}

const CongressDetail: React.FC = () => {
  const { number } = useParams<{ number: string }>();
  const congress = Number(number);
  const navigate = useNavigate();

  const [bills, setBills] = useState<BillItem[]>([]);
  const [members, setMembers] = useState<MemberItem[]>([]);
  const [membersTotal, setMembersTotal] = useState(0);
  const [membersLimit, setMembersLimit] = useState(60);
  const [membersOffset, setMembersOffset] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const resp = await apiService.getCongressDetail(congress, {
          top_n: 50,
          members_limit: membersLimit,
          members_offset: membersOffset,
        });
        if (!cancelled) {
          setBills(resp.significant_legislation || []);
          const m = resp.members || { items: [], total: 0 };
          setMembers(m.items || []);
          setMembersTotal(m.total || 0);
        }
      } catch (e) {
        if (!cancelled) {
          setBills([]);
          setMembers([]);
          setMembersTotal(0);
        }
      }
    };
    if (Number.isFinite(congress)) {
      load();
    }
    return () => { cancelled = true; };
  }, [congress, membersLimit, membersOffset]);

  const page = Math.floor(membersOffset / membersLimit) + 1;
  const pageCount = Math.max(1, Math.ceil(membersTotal / membersLimit));

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h4">{ordinal(congress)} Congress</Typography>
        <Button onClick={() => navigate('/congress')}>Back to all</Button>
      </Stack>

      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Significant Legislation</Typography>
      <Grid container spacing={2}>
        {bills.map((b) => {
          const to = `/bills/${b.congress}/${b.bill_type}/${b.bill_number}`;
          const label = `${(b.bill_type || '').toUpperCase()} ${b.bill_number}`;
          return (
            <Grid item xs={12} md={6} key={`${b.bill_type}-${b.bill_number}`}>
              <Card sx={{ height: '100%', cursor: 'pointer' }} onClick={() => navigate(to)}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
                  <Typography variant="h6" sx={{ mt: 0.5 }}>{b.title || label}</Typography>
                  {b.latest_action_text && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{b.latest_action_text}</Typography>
                  )}
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    {typeof b.cosponsors_count === 'number' && (
                      <Chip label={`${b.cosponsors_count} cosponsors`} size="small" />
                    )}
                    {b.sponsor_name && (<Chip label={`Sponsor: ${b.sponsor_name}`} size="small" />)}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>Members</Typography>
      <Grid container spacing={2}>
        {members.map((m) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={m.bioguide_id}>
            <Card sx={{ height: '100%', cursor: 'pointer' }} onClick={() => navigate(`/members/${m.bioguide_id}`)}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar src={m.image_url || undefined} alt={`${m.first_name} ${m.last_name}`} />
                  <Box>
                    <Typography variant="subtitle1">{m.first_name} {m.last_name}</Typography>
                    <Typography variant="body2" color="text.secondary">{m.party || ''} {m.state || ''}{m.district ? `-${m.district}` : ''}</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Pagination count={pageCount} page={page} onChange={(_, p) => setMembersOffset((p - 1) * membersLimit)} />
      </Box>
    </Box>
  );
};

export default CongressDetail;
